import moment from 'moment'
import dateUtil from 'element-ui/lib/utils/date'
import { t } from 'element-ui/lib/locale'

const weeks = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const getI18nSettings = () => {
  return {
    dayNamesShort: weeks.map(week => t(`el.datepicker.weeks.${week}`)),
    dayNames: weeks.map(week => t(`el.datepicker.weeks.${week}`)),
    monthNamesShort: months.map(month => t(`el.datepicker.months.${month}`)),
    monthNames: months.map((month, index) => t(`el.datepicker.month${index + 1}`)),
    amPm: ['am', 'pm']
  }
}

const newArray = function (start, end) {
  let result = []
  for (let i = start; i <= end; i++) {
    result.push(i)
  }
  return result
}

export const toDate = function (date) {
  return isDate(date) ? new Date(date) : null
}

export const isDate = function (date) {
  if (date === null || date === undefined) return false
  if (isNaN(new Date(date).getTime())) return false
  if (Array.isArray(date)) return false // deal with `new Date([ new Date() ]) -> new Date()`
  return true
}

export const isDateObject = function (val) {
  return val instanceof Date
}

export const formatDate = function (date, format) {
  date = toDate(date)
  if (!date) return ''
  return dateUtil.format(date, format || 'yyyy-MM-dd', getI18nSettings())
}

export const parseDate = function (string, format) {
  return dateUtil.parse(string, format || 'yyyy-MM-dd', getI18nSettings())
}

export const getDayCountOfMonth = function (year, month) {
  if (month === 3 || month === 5 || month === 8 || month === 10) {
    return 30
  }

  if (month === 1) {
    // eslint-disable-next-line
    if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
      return 29
    } else {
      return 28
    }
  }

  return 31
}

export const getDayCountOfYear = function (year) {
  const isLeapYear = year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)
  return isLeapYear ? 366 : 365
}

export const getFirstDayOfMonth = function (date) {
  const temp = new Date(date.getTime())
  temp.setDate(1)
  return temp.getDay()
}

// see: https://stackoverflow.com/questions/3674539/incrementing-a-date-in-javascript
// {prev, next} Date should work for Daylight Saving Time
// Adding 24 * 60 * 60 * 1000 does not work in the above scenario
export const prevDate = function (date, amount = 1) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - amount)
}

export const nextDate = function (date, amount = 1) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

export const getStartDateOfMonth = function (year, month) {
  const result = new Date(year, month, 1)
  const day = result.getDay()

  if (day === 0) {
    return prevDate(result, 7)
  } else {
    return prevDate(result, day)
  }
}

export const getWeekNumber = function (src) {
  if (!isDate(src)) return null
  const date = new Date(src.getTime())
  date.setHours(0, 0, 0, 0)
  // Thursday in current week decides the year.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7))
  // January 4 is always in week 1.
  const week1 = new Date(date.getFullYear(), 0, 4)
  // Adjust to Thursday in week 1 and count number of weeks from date to week 1.
  // Rounding should be fine for Daylight Saving Time. Its shift should never be more than 12 hours.
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
}

export const getRangeHours = function (ranges) {
  const hours = []
  let disabledHours = []

  ;(ranges || []).forEach(range => {
    const value = range.map(date => date.getHours())

    disabledHours = disabledHours.concat(newArray(value[0], value[1]))
  })

  if (disabledHours.length) {
    for (let i = 0; i < 24; i++) {
      hours[i] = disabledHours.indexOf(i) === -1
    }
  } else {
    for (let i = 0; i < 24; i++) {
      hours[i] = false
    }
  }

  return hours
}

export const range = function (n) {
  // see https://stackoverflow.com/questions/3746725/create-a-javascript-array-containing-1-n
  return Array.apply(null, { length: n }).map((_, n) => n)
}

export const modifyDate = function (date, y, m, d) {
  return new Date(y, m, d, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds())
}

export const modifyTime = function (date, h, m, s) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, s, date.getMilliseconds())
}

export const modifyWithTimeString = (date, time) => {
  if (date == null || !time) {
    return date
  }
  time = parseDate(time, 'HH:mm:ss')
  return modifyTime(date, time.getHours(), time.getMinutes(), time.getSeconds())
}

export const clearTime = function (date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export const clearMilliseconds = function (date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    0
  )
}

export const limitTimeRange = function (date, ranges, format = 'HH:mm:ss') {
  // TODO: refactory a more elegant solution
  if (ranges.length === 0) return date
  const normalizeDate = date => dateUtil.parse(dateUtil.format(date, format), format)
  const ndate = normalizeDate(date)
  const nranges = ranges.map(range => range.map(normalizeDate))
  if (nranges.some(nrange => ndate >= nrange[0] && ndate <= nrange[1])) return date

  let minDate = nranges[0][0]
  let maxDate = nranges[0][0]

  nranges.forEach(nrange => {
    minDate = new Date(Math.min(nrange[0], minDate))
    maxDate = new Date(Math.max(nrange[1], minDate))
  })

  const ret = ndate < minDate ? minDate : maxDate
  // preserve Year/Month/Date
  return modifyDate(ret, date.getFullYear(), date.getMonth(), date.getDate())
}

export const timeWithinRange = function (date, selectableRange, format) {
  const limitedDate = limitTimeRange(date, selectableRange, format)
  return limitedDate.getTime() === date.getTime()
}

export const changeYearMonthAndClampDate = function (date, year, month) {
  // clamp date to the number of days in `year`, `month`
  // eg: (2010-1-31, 2010, 2) => 2010-2-28
  const monthDate = Math.min(date.getDate(), getDayCountOfMonth(year, month))
  return modifyDate(date, year, month, monthDate)
}

export const prevMonth = function (date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  return month === 0
    ? changeYearMonthAndClampDate(date, year - 1, 11)
    : changeYearMonthAndClampDate(date, year, month - 1)
}

export const nextMonth = function (date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  return month === 11
    ? changeYearMonthAndClampDate(date, year + 1, 0)
    : changeYearMonthAndClampDate(date, year, month + 1)
}

export const prevYear = function (date, amount = 1) {
  const year = date.getFullYear()
  const month = date.getMonth()
  return changeYearMonthAndClampDate(date, year - amount, month)
}

export const nextYear = function (date, amount = 1) {
  const year = date.getFullYear()
  const month = date.getMonth()
  return changeYearMonthAndClampDate(date, year + amount, month)
}

export const extractDateFormat = function (format) {
  return format
    .replace(/\W?m{1,2}|\W?ZZ/g, '')
    .replace(/\W?h{1,2}|\W?s{1,3}|\W?a/gi, '')
    .trim()
}

export const extractTimeFormat = function (format) {
  return format.replace(/\W?D{1,2}|\W?Do|\W?d{1,4}|\W?M{1,4}|\W?y{2,4}/g, '').trim()
}

export function tailWeekCheck(value, format) {
  if (!value) return ''
  let week = getWeekNumber(value)
  let month = value.getMonth()
  const trueDate = new Date(value)
  if (week === 1 && month === 11) {
    trueDate.setHours(0, 0, 0, 0)
    trueDate.setDate(trueDate.getDate() + 3 - ((trueDate.getDay() + 6) % 7))
  }
  let date = formatDate(trueDate, format)
  date = /WW/.test(date) ? date.replace(/WW/, week < 10 ? '0' + week : week) : date.replace(/W/, week)
  date = date.replace(/w/, '-') + '周'
  return date
}

export function correctDatesOrder(dates) {
  if (!Array.isArray(dates) || dates.length !== 2 || dates.some(val => !isDate(val))) {
    return dates
  }
  // 将字符串和 Date 类型统一转换为 moment 对象，并排序
  const moments = dates.map(date => moment(date))

  moments.sort((a, b) => (a.isAfter(b) ? 1 : -1))

  // 将排序后的 moment 对象转换回 Date 类型
  const correctedDates = moments.map(m => m.toDate())

  return correctedDates
}

export const DEFAULT_FORMATS = {
  date: 'yyyy-MM-dd',
  month: 'yyyy-MM',
  months: 'yyyy-MM',
  datetime: 'yyyy-MM-dd HH:mm',
  time: 'HH:mm',
  week: 'yyyywWW',
  weekrange: 'yyyywWW',
  timerange: 'HH:mm',
  daterange: 'yyyy-MM-dd',
  monthrange: 'yyyy-MM',
  datetimerange: 'yyyy-MM-dd HH:mm',
  year: 'yyyy',
  years: 'yyyy',
  valueFormat: 'yyyy-MM-dd HH:mm:ss'
}

export const FORMAT = (val, fmt) => {
  // 类型为datas，val是数组
  if (fmt === 'timestamp' || Array.isArray(val)) {
    return val
  } else {
    return time(val, fmt.replace(/y/g, 'Y').replace(/d/g, 'D'))
  }
}

/*
 特写关于日期组件在有通用查询的, 包括范围日期的开始时间和结束时间特写。
 特写逻辑来自于：老日期组件（kye-date-picker）
*/
export function dateFormatCallback(val, vm) {
  const { operation, type } = vm
  const valueFormat = vm.currentValueFormat
  const format = vm.currentFormat
  const isFormat = typeof val === 'string' && operation && valueFormat === DEFAULT_FORMATS.valueFormat
  if (isFormat) {
    // 通用查询 条件关系
    if (type === 'date') {
      switch (operation) {
        case 'greaterthan': // '>'
        case 'lessthanorequal': // '<='
          val = moment(val)
            .set({
              hour: 23,
              minute: 59,
              second: 59
            })
            .format('YYYY-MM-DD HH:mm:ss')
          break
        case 'lessthan': // '<'
        case 'greaterthanorequal': // '>='
          val = moment(val)
            .set({
              hour: 0,
              minute: 0,
              second: 0
            })
            .format('YYYY-MM-DD HH:mm:ss')
          break
      }
    } else if (type === 'datetime' && operation === 'between' && format !== valueFormat) {
      let end = val.includes('23:59') ? ':59' : ':00'
      val = val.replace(/:\d{2}$/, end)
    }
  }
  if (['datetimerange', 'daterange', 'monthrange', 'weekrange'].includes(type)) {
    // 日期范围
    if (Array.isArray(val) && val.length === 2) {
      let d = JSON.parse(JSON.stringify(val))
      if (type === 'daterange' || type === 'weekrange') {
        if (d[0]) {
          d[0] = d[0].slice(0, 10) + ' 00:00:00'
        }
        if (d[1]) {
          d[1] = d[1].slice(0, 10) + ' 23:59:59'
        }
        val = d
      } else if (type === 'datetimerange' && val[0] && val[1] && val[0] === val[1]) {
        d[1] = d[1].slice(0, 10) + ' 23:59:59'
        val = d
      } else if (type === 'monthrange') {
        d[0] = d[0].slice(0, 7)
        d[1] = d[1].slice(0, 7)
        val = d
      }
      if (['datetimerange', 'daterange', 'monthrange'].includes(type) && !val[0] && !val[1]) {
        val = []
      }
      val = val.map(v => FORMAT(v, valueFormat))
    }
  }
  if (type === 'months') {
    val = (val || []).map(v => FORMAT(v, 'yyyy-MM'))
  } else if (type === 'dates') {
    val = (val || []).map(v => FORMAT(v, 'yyyy-MM-dd'))
  }
  // if (['week', 'weekrange'].includes(type) && val) {
  //   val = Array.isArray(val) ? val.map(i => i.replace('周', '')) : val.replace('周', '')
  // }
  return val
}

export const VALUE_FORMAT = 'yyyy-MM-dd HH:mm:ss'
export const date = (date, fmt = 'YYYY-MM-DD') => {
  let str = date || ''
  if (typeof str === 'string' && str && (str.includes('**') || str.includes('✽✽'))) {
    return str
  }
  return str ? moment(str).format(fmt) : str
}

export const time = (d, fmt = 'YYYY-MM-DD HH:mm:ss') => {
  return date(d, fmt)
}



// WEBPACK FOOTER //
// ./src/public/components/kye-date-picker3/src/util/index.js